import React, { ReactElement, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { makeStyles } from '@mui/styles';
import {
	FormControlLabel,
	IconButton,
	Pagination,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from '@mui/material';
import { colors, theme } from '../../theme';
import { CUDepartmentMappingType, CUDepartmentRequestType, CUMasterDataType } from '../../types/cu.types';
import { PostResponse } from '../../types/common.types';
import { CustomAlert } from '../shared/alert/alert';
import { CustomButton } from '../shared/button/button';
import { Loader } from '../shared/loader/loader';
import { MultiSelectDropDown } from '../shared/dropdown/multiSelect';
import { ToolTip } from '../shared/tooltip/tooltip';
import { Search } from '../shared/search/search';
import { Status } from '../../config/cuTabConfig';
import { SingleSelectDropDown } from '../shared/dropdown/singleSelect';
import { TextBox } from '../shared/textbox/textbox';
import { getDepartmentMapping, saveDepartmentMapping } from '../../services/cu-service';
import { useAsyncOperation } from '../../utils/use-async-operation';
import { msalInstance } from '../../config/msalConfig';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

export const testid = 'department';

const color = colors();
const themes = theme().typography;
export const Department = ({ master }: CUMasterDataType): ReactElement => {
	const classes = useStyles();
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.BrandSCAdmin') || false;
	const productCategoryList = [
		{ keyId: -1, name: "Can't be specified" },
		...master.productCategory.map((item) => ({
			...item,
			name: item.name.replace('__', ' - '),
		})),
	];
	const userAccount = msalInstance.getActiveAccount();
	const loggedInUser = userAccount ? `${userAccount.name?.split('(')[0]}| ${userAccount.username}` : '';

	const [initialData, setInitialData] = useState<CUDepartmentMappingType[]>([]);
	const [deptData, setDeptData] = useState<CUDepartmentMappingType[]>([]);
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState<number>(25);
	const [pageCount, setPageCount] = useState<number>(1);
	const [openErrorAlert, setOpenErrorAlert] = useState(false);
	const [openSaveAlert, setOpenSaveAlert] = useState(false);
	const [rowData, setRowData] = useState<CUDepartmentMappingType>();
	const [disableActions, setDisableActions] = useState<boolean>(false);
	const [prodCategory, setProdCategory] = useState<string[]>([]);
	const [status, setStatus] = useState<string[]>([]);
	const [searchText, setSearchText] = useState<string>();
	const [showBlanks, setShowBlanks] = useState(false);
	const [category, setCategory] = useState<string>();
	const [comment, setComment] = useState<string>();
	const initialLoadTriggered = useRef(false);

	const [getDepartments, deptBusy, department, deptError] =
		useAsyncOperation<CUDepartmentMappingType[]>(getDepartmentMapping);
	const [saveDepartment, saveBusy, saveData, saveError] = useAsyncOperation<PostResponse>(saveDepartmentMapping);

	useEffect(() => {
		if (initialLoadTriggered.current) return;
		initialLoadTriggered.current = true;
		getDepartments();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (deptError) setOpenErrorAlert(true);
	}, [deptError]);

	useEffect(() => {
		if (saveData) {
			getDepartments();
			setOpenSaveAlert(true);
			setDisableActions(false);
		}
	}, [saveData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (saveError) setOpenSaveAlert(true);
	}, [saveError]);

	useEffect(() => {
		if (department) {
			setShowBlanks(false);
			onClearClick();
			const sortedData: CUDepartmentMappingType[] = [...department].sort(
				(a, b) => new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime()
			);
			setInitialData(sortedData);
			setDeptData(sortedData);
			setPageCount(Math.floor(sortedData.length / rowsPerPage));
		}
	}, [department]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (deptData) setPageCount(Math.floor(deptData.length / rowsPerPage));
	}, [rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		onClearClick();
	}, [showBlanks]); // eslint-disable-line react-hooks/exhaustive-deps

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const handleRowPerPageChange = (value: string) => {
		setRowsPerPage(parseInt(value));
		setPage(1);
	};

	const onClearClick = () => {
		setPage(1);
		if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
			try {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} catch {
				window.scrollTo(0, 0);
			}
		}
		setProdCategory([]);
		setStatus([]);
		setSearchText(undefined);

		if (showBlanks && initialData) {
			let blankData = initialData.filter((d) => d.cuProductCategory === null);
			setPageCount(Math.floor(blankData.length / rowsPerPage));
			setDeptData(blankData);
		} else {
			setDeptData(initialData || []);
			!showBlanks && setPageCount(Math.floor(initialData.length / rowsPerPage));
		}
	};

	const onSearchClick = () => {
		if (prodCategory.length !== 0 || status.length !== 0 || searchText) {
			let filteredData = initialData || [];
			setPage(1);
			if (prodCategory.length > 0 && filteredData.length > 0) {
				var pc = prodCategory.map((pc) => pc.substring(0, pc.indexOf(' - ')));
				filteredData = filteredData.filter((d) =>
					pc.includes(d.cuProductCategory?.substring(0, d.cuProductCategory?.indexOf('__')))
				);
			}
			if (status.length === 1 && filteredData.length > 0) {
				filteredData = filteredData.filter((d) => (status[0] === 'Active' ? d.active : !d.active));
			}
			if (searchText && filteredData.length > 0) {
				filteredData = filteredData.filter(
					(d) =>
						d.departmentName.toLowerCase().includes(searchText.toLowerCase()) ||
						d.departmentId.includes(searchText) ||
						d.comments?.includes(searchText)
				);
			}
			if (showBlanks) filteredData = filteredData.filter((d) => d.cuProductCategory === null);

			setDeptData(filteredData);
			setPageCount(Math.floor(filteredData.length / rowsPerPage));
		}
	};

	const handleAddEditClick = (row: CUDepartmentMappingType, isEdit: boolean) => {
		let data = [...deptData];
		data.filter((d) => d.departmentId === row.departmentId)[0].isModify = true;
		if (isEdit) {
			data.filter((d) => d.departmentId === row.departmentId)[0].isEdit = true;
			setCategory(
				productCategoryList.filter(
					(rm) =>
						rm.name.substring(0, rm.name.indexOf(' - ')) ===
						row.cuProductCategory.substring(0, row.cuProductCategory.indexOf('__'))
				)[0]?.name || undefined
			);
			setComment(row.comments);
		}
		setDeptData(data);
		setRowData(row);
		setDisableActions(true);
	};

	const onDiscardClick = (deptId: string) => {
		let data = [...deptData];
		data.filter((d) => d.departmentId === deptId)[0].isModify = false;
		data.filter((d) => d.departmentId === deptId)[0].isEdit = false;
		setDeptData(data);
		setDisableActions(false);
		setCategory(undefined);
		setComment(undefined);
	};

	const onSaveClick = () => {
		if (rowData && category) {
			const requestData: CUDepartmentRequestType = {
				id: rowData.isEdit ? rowData.id : null,
				departmentId: rowData.departmentId || '',
				departmentName: rowData.departmentName || '',
				cuProductCategory: category.replace(' - ', '__'),
				comments: comment || '',
				createdDate: rowData.isEdit ? rowData.createdDate : null,
				updatedBy: loggedInUser,
			};
			saveDepartment(requestData);
		}
	};

	return (
		<>
			<Loader key={`${testid}-loader`} open={deptBusy || saveBusy} message="Loading..." />
			{openErrorAlert && (
				<CustomAlert
					open={openErrorAlert}
					message="Something went wrong while retrieving department mapping data!"
					severity="error"
					timeout={5000}
					isFade={false}
					onCloseCallback={() => {
						setOpenErrorAlert(false);
					}}
				/>
			)}
			{openSaveAlert && (
				<div className={classes.alert}>
					<CustomAlert
						open={openSaveAlert}
						timeout={7000}
						message={saveData && !saveError ? 'Mapping saved successfully!' : 'Something went wrong!'}
						severity={saveData && !saveError ? 'success' : 'error'}
						onCloseCallback={() => {
							setOpenSaveAlert(false);
						}}
					/>
				</div>
			)}
			<div className={classes.filters}>
				{!showBlanks && (
					<MultiSelectDropDown
						label="Product Category"
						width={185}
						data={productCategoryList.map((list) => list.name)}
						value={prodCategory}
						hasSearch
						onSelectCallback={setProdCategory}
					/>
				)}
				<MultiSelectDropDown
					label="Department Status"
					width={185}
					data={Status.map((list) => list.value?.toString() || '')}
					value={status}
					onSelectCallback={setStatus}
				/>
				<Search placeholder="H&M Dept. Name, ID, Comments" value={searchText || ''} onSearchCallback={setSearchText} />
				<CustomButton id="search" text="Search" width={90} variant="outlined" onClickCallback={onSearchClick} />
				{(prodCategory.length > 0 || status.length > 0 || searchText !== undefined) && (
					<CustomButton id="clear" text="Clear" width={90} onClickCallback={onClearClick} />
				)}
				<FormControlLabel
					className={classes.label}
					control={
						<Switch
							checked={showBlanks}
							onClick={() => {
								setShowBlanks(!showBlanks);
								setPage(1);
							}}
							data-testid={`${testid}-blanks`}
						/>
					}
					label="Only Blanks"
					labelPlacement="start"
				/>
			</div>

			{deptData && (
				<>
					<TableContainer className={classes.table} data-testid={`${testid}-table`}>
						<Table stickyHeader>
							<TableHead>
								<TableRow key={`${testid}-header`}>
									<TableCell width="9%">Department ID</TableCell>
									<TableCell width="20%">Department Name</TableCell>
									<TableCell width="20%">TE Product Category</TableCell>
									<TableCell width="10%">Mapped Date</TableCell>
									<TableCell width="20%">Comments</TableCell>
									{isAdmin && <TableCell width="1%" />}
								</TableRow>
							</TableHead>
							<TableBody data-testid={`${testid}-body`}>
								{(rowsPerPage > 0
									? deptData.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
									: deptData
								).map((row, i) => (
									<TableRow key={i} hover={row.isModify ? false : true}>
										{row.isModify ? (
											<>
												<TableCell className={classes.modifyRow}>{row.departmentId}</TableCell>
												<TableCell>{row.departmentName}</TableCell>
												<TableCell
													sx={{
														textWrap: 'nowrap',
														maxWidth: '100px',
													}}
												>
													<div>
														<SingleSelectDropDown
															labelText="Product Category"
															label=""
															data={productCategoryList.map((item) => ({
																...item,
																key: Number(item.keyId),
																value: item.name,
															}))}
															value={category || ''}
															width={325}
															hasSearch
															onSelectCallback={setCategory}
														/>
														<TextBox
															id={`${testid}-comment`}
															label=""
															labelText="Comments"
															width={332}
															value={comment || ''}
															onChangeCallback={setComment}
														/>
													</div>
													<div className={classes.actions}>
														{!category && (
															<CustomButton
																id="save-disabled"
																disabled={true}
																text="Save"
																data-testid={`${testid}-save-disabled`}
															/>
														)}
														{category && (
															<CustomButton
																id="save"
																text="Save"
																onClickCallback={onSaveClick}
																data-testid={`${testid}-save`}
															/>
														)}
														<CustomButton
															id="discard"
															text="Discard"
															onClickCallback={() => onDiscardClick(row.departmentId)}
															data-testid={`${testid}-discard`}
														/>
													</div>
												</TableCell>
												<TableCell />
												<TableCell />
												<TableCell />
											</>
										) : (
											<>
												<TableCell className={!row.active ? classes.inactive : ''}>{row.departmentId}</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													<ToolTip
														tooltipText={row.active ? '' : 'This Department is inactive'}
														children={<span>{row.departmentName}</span>}
													/>
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													{row.cuProductCategory?.replace('__', ' - ')}
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													{row.updatedDate ? dayjs(row.updatedDate).format('DD/MM/YYYY') : ''}
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>{row.comments || ''}</TableCell>
												{isAdmin && (
													<TableCell align="right">
														{row.active && row.cuProductCategory === null && (
															<ToolTip
																tooltipText="Add mapping"
																children={
																	<span>
																		<IconButton
																			id="add-mapping"
																			className={classes.iconButton}
																			disabled={disableActions}
																			onClick={() => handleAddEditClick(row, false)}
																		>
																			<AddIcon
																				fontSize="inherit"
																				sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																			/>
																		</IconButton>
																	</span>
																}
															/>
														)}
														{row.active && row.cuProductCategory !== null && (
															<ToolTip
																tooltipText="Edit mapping"
																children={
																	<span>
																		<IconButton
																			id="edit-mapping"
																			className={classes.iconButton}
																			disabled={disableActions}
																			onClick={() => handleAddEditClick(row, true)}
																		>
																			<EditOutlinedIcon
																				sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																			/>
																		</IconButton>
																	</span>
																}
															/>
														)}
													</TableCell>
												)}
											</>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						data-testid={`${testid}-pagination`}
						className={classes.pagination}
						rowsPerPageOptions={[25, 50, 100]}
						rowsPerPage={rowsPerPage}
						count={deptData.length}
						page={page - 1}
						onPageChange={() => {}}
						onRowsPerPageChange={(e) => handleRowPerPageChange(e.target.value)}
						ActionsComponent={() => (
							<Pagination
								data-testid={`${testid}-node`}
								count={deptData.length % rowsPerPage === 0 ? pageCount : pageCount + 1}
								page={page}
								onChange={handlePageChange}
								shape="rounded"
							/>
						)}
					/>
				</>
			)}
		</>
	);
};

const useStyles = makeStyles(() => ({
	filters: {
		paddingTop: '10px',
		'& #search': {
			backgroundColor: color.primary[100],
			color: color.text.light,
		},
	},
	table: {
		height: 'calc(100% - 205px)',
		'& .MuiTableCell-head': {
			...themes.bodyMedium,
			color: color.neutral[8],
			backgroundColor: color.neutral[1],
			borderTop: `1px solid ${color.neutral[2]}`,
		},
		'& .MuiTableCell-body': {
			...themes.bodyRegular,
			color: color.text.dark,
			padding: '3px 8px !important',
		},
		'& .MuiTableCell-root': {
			borderBottom: `1px solid ${color.neutral[2]}`,
			padding: '13px 8px',
		},
		'& .MuiTableRow-hover': {
			'&:hover': {
				backgroundColor: `${color.signal.infoLight} !important`,
			},
		},
	},
	pagination: {
		'& .MuiToolbar-root.MuiTablePagination-toolbar': {
			position: 'absolute',
			right: 20,
			paddingTop: '5px',
		},
		'& .MuiButtonBase-root.Mui-selected': {
			backgroundColor: color.primary[100],
			color: color.shades.white,
		},
		'& .MuiButtonBase-root.Mui-selected:hover': {
			backgroundColor: color.primary[100],
		},
		'& .MuiPagination-root.MuiPagination-text': {
			flexShrink: 0,
			marginLeft: '10px',
		},
	},
	iconButton: {
		padding: '5px !important',
		'& svg': {
			width: '16px',
			height: '16px',
		},
	},
	inactive: {
		color: `${color.neutral[5]} !important`,
	},
	label: {
		position: 'absolute',
		right: '30px',
		paddingTop: '8px',
		'& .MuiTypography-root': {
			...themes.bodyRegular,
		},
	},
	modifyRow: {
		borderLeft: `5px solid ${color.signal.info}`,
	},
	actions: {
		'& #discard': {
			backgroundColor: color.primary[5],
			color: color.text.dark,
			border: 'none',
		},
		'& #save-disabled': {
			backgroundColor: color.neutral[6],
			color: color.text.light,
			border: 'none',
		},
		'& #save': {
			backgroundColor: color.primary[100],
			color: color.text.light,
			border: 'none',
		},
	},
	alert: {
		position: 'fixed',
		top: '10px',
		left: '50%',
		transform: 'translateX(-50%)',
	},
}));
